import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar, ChevronDown, Check, Minus } from 'lucide-react';
import { useDismissibleLayer } from '@/hooks/ui/useDismissibleLayer';
import { cn } from '@/lib/infra/utils';
import type { Sport, MarketType, DateFilter, SlateRange, StatusFilter, MarketSide, PriceMode } from './types';
import { SPORT_TABS, MARKET_TYPE_TABS, SPORT_LABEL, CFB_BUCKET_LABEL } from './types';
import type { DashboardSport } from '@/lib/routing/dashboardSport';
import { ATP_CHALLENGER_DASHBOARD_SPORT, cfbBucketOf } from '@/lib/routing/dashboardSport';

/** Dashboard-local tab inventory (#604): SPORT_TABS plus the ATP Challenger
 *  circuit chip, inserted beside ATP. SPORT_TABS itself stays untouched — the
 *  dashboard-only key must not leak into settings, trackers, or exports. */
export const DASHBOARD_SPORT_TABS: ReadonlyArray<{ id: DashboardSport; label: string }> =
  SPORT_TABS.flatMap((tab): Array<{ id: DashboardSport; label: string }> =>
    tab.id === 'atp'
      ? [tab, { id: ATP_CHALLENGER_DASHBOARD_SPORT, label: 'ATP Challenger' }]
      : [tab],
  );

/** CFB subdivision buckets (#653), rendered in their OWN chip group and only
 *  while the CFB board is selected — the group's appearance on a CFB click is
 *  what reads as a drill-down. 'All CFB' leads so the parent is named inside
 *  the child control and the unfiltered default stays one click away.
 *  Deliberately NOT part of DASHBOARD_SPORT_TABS: a bucket is not a sport, and
 *  must never appear in the sport row. */
const CFB_BUCKET_TABS: ReadonlyArray<{ id: DashboardSport; label: string }> = [
  { id: 'cfb', label: `All ${SPORT_LABEL.cfb}` },
  { id: 'cfb-fbs', label: CFB_BUCKET_LABEL.fbs },
  { id: 'cfb-fcs', label: CFB_BUCKET_LABEL.fcs },
  { id: 'cfb-other', label: CFB_BUCKET_LABEL.other },
];

interface DashboardToolbarProps {
  selectedSport: DashboardSport;
  /** Empty set means "All sports" on moneyline; non-empty is the single
   *  active moneyline sport. Non-moneyline views stay single-sport. */
  selectedSports: ReadonlySet<DashboardSport>;
  /** Sports with at least one live market frame in the store. Sport chips
   *  outside this set are hidden so the toolbar doesn't advertise inactive
   *  tabs. The currently-selected sport is always included by the parent
   *  so the active chip stays visible even before its first frame arrives. */
  activeSports: Set<DashboardSport>;
  selectedMarketType: MarketType;
  selectedDate: string;
  /** #712 — the weekly slate's inclusive range, e.g. `Sep 7 – Sep 13`, shown
   *  in place of the single date. Null in Day mode (and for any window that
   *  can't be named), which leaves the pill on `selectedDate` exactly as
   *  before. The toolbar renders this and never derives it: the window belongs
   *  to the board, so the label and the rows can't come from different
   *  arithmetic. */
  slateRangeLabel: string | null;
  /** True only when the displayed window CONTAINS the actual current date AND
   *  dateFilter is 'today'. Drives both the Today chip's active state
   *  and the date-row text color, so the "you're looking at today"
   *  signal is a single visual concept instead of two competing
   *  indicators (mode chip vs. shown date). Containment rather than equality
   *  so a weekly slate holding today doesn't dim the chip on a Wednesday. */
  isToday: boolean;
  /** #712 — the range the board is ACTUALLY running on, already reduced to
   *  'day' wherever a weekly slate doesn't apply. Read-only here; the parent
   *  owns the write so the inert-off-board rule lives in one place. */
  slateRange: SlateRange;
  /** #712 — whether a weekly slate applies to the current board at all. False
   *  hides the control outright rather than rendering it inert, the same fence
   *  `kalshiMarketsOnlyEligible` and `cfbSavedOnlyEligible` use: the slate is
   *  scoped to the NFL and CFB game boards, so offering it on MLB or on a
   *  championship future would be a lie. */
  slateRangeEligible: boolean;
  onSlateRangeChange: (range: SlateRange) => void;
  dateFilter: DateFilter;
  statusFilter: StatusFilter;
  marketSide: MarketSide;
  priceMode: PriceMode;
  showAllSportsChip: boolean;
  /** Binary moneyline sports currently on the board — the "Hide empty" menu's
   *  toggle rows (#213). Empty means no eligible sport, so the whole control is
   *  hidden. Arrives in canonical SPORT_TABS order. */
  eligibleEmptySports: Sport[];
  /** Persisted per-sport suppression list. Drives each menu row's check and
   *  the "All sports" checked / partial / empty state. Read-only here — the
   *  parent owns writes so the off-board-preservation rule lives in one place. */
  hiddenEmptySports: readonly Sport[];
  onToggleHiddenEmptySport: (sport: Sport) => void;
  onToggleAllEmptySports: () => void;
  /** #509 — whether the "Kalshi markets only" control applies to the current
   *  board at all. False hides it outright rather than rendering an inert
   *  control: the filter is scoped to the moneyline board (same fence as
   *  #212), so advertising it on Spreads/Totals/Futures would be a lie. */
  kalshiMarketsOnlyEligible: boolean;
  /** #509 — persisted opt-in state. Read-only here; the parent owns the write. */
  kalshiMarketsOnly: boolean;
  onToggleKalshiMarketsOnly: () => void;
  /** True when a CFB MATCHUP board is on screen — as the whole slate or as one
   *  subdivision bucket. Gates both CFB-scoped controls: the #653 bucket chips
   *  and the #654 Saved-only chip.
   *
   *  Owned by the parent rather than re-derived here (#654), because the
   *  dashboard needs the same answer to scope the lens filters and the two
   *  must not drift: a toolbar that offers a control the board doesn't apply —
   *  or applies one it doesn't offer — is the failure this fence exists to
   *  prevent. Excludes "All sports" (no sub-board applies) and Futures (a
   *  subdivision describes a MATCHUP, and a championship future is one market
   *  listing many teams). */
  cfbBoardSelected: boolean;
  /** #654 — whether saved-only applies to the current board at all. Narrower
   *  than `cfbBoardSelected`, and deliberately a separate prop rather than
   *  reusing it: the lens is a view over the #120 SAVED LIST, and that list has
   *  no control and no strip on the Spread/Total board — you cannot save a game
   *  there. Offering the chip there would filter a board down to a set it gives
   *  you no way to populate or clear. Same fence, same shape, as
   *  `kalshiMarketsOnlyEligible`.
   *
   *  Note this is NOT true of #653's bucket chips above, which stay on the
   *  wider gate: a subdivision is a property of the game, derivable from codes
   *  already on every row, so it needs no affordance and reaches every board. */
  cfbSavedOnlyEligible: boolean;
  /** #654 — persisted opt-in state. Read-only here; the parent owns the write
   *  so the preserved-off-board rule lives in one place. */
  cfbSavedOnly: boolean;
  onToggleCfbSavedOnly: () => void;
  onSportToggle: (sport: DashboardSport) => void;
  onAllSportsClick: () => void;
  onMarketTypeChange: (mt: MarketType) => void;
  onDateChange: (date: string) => void;
  onDatePrev: () => void;
  onDateNext: () => void;
  onDateFilterChange: (f: DateFilter) => void;
  onTodayClick: () => void;
  onStatusFilterChange: (f: StatusFilter) => void;
  onMarketSideChange: (side: MarketSide) => void;
  onPriceModeChange: (mode: PriceMode) => void;
}

function ChipGroup({
  children,
  className,
  label,
}: {
  children: React.ReactNode;
  className?: string;
  /** Names the group for assistive tech. The sport group needs none (it is the
   *  toolbar's default subject); a nested group does — "CFB subdivision" is
   *  what tells a screen reader these chips refine the sport above. */
  label?: string;
}) {
  return (
    <div
      className={cn('bg-muted/50 border-border/50 flex items-center rounded-lg border p-1', className)}
      role={label ? 'group' : undefined}
      aria-label={label}
    >
      {children}
    </div>
  );
}

function Chip({
  active,
  onClick,
  activeClass = 'bg-primary text-primary-foreground',
  children,
}: {
  active: boolean;
  onClick: () => void;
  activeClass?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      // A chip is a toggle, and its selected state is carried by color alone —
      // aria-pressed is the only thing that states it non-visually.
      aria-pressed={active}
      className={cn(
        'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
        active ? activeClass : 'text-muted-foreground hover:text-foreground'
      )}
    >
      {children}
    </button>
  );
}

/** Compact per-sport "Hide empty" control (#213), replacing the #192 tennis-only
 *  chip. A single Chip-styled trigger opens a checkbox menu: "All sports"
 *  (select/clear every eligible sport) plus one row per eligible sport. The
 *  trigger reads active whenever ≥1 eligible sport is currently suppressed.
 *
 *  Presentational only — membership and the All state are derived from the two
 *  arrays the parent hands down, and toggles fire straight back up. "All" is
 *  never a persisted flag; it reflects the eligible sports' membership and the
 *  parent's clear/select-all handler preserves off-board entries. Closes on
 *  Escape (restoring focus to the trigger) and on an outside click; stays open
 *  across toggles so several sports can be flipped in one pass. */
function HideEmptyMenu({
  eligibleEmptySports,
  hiddenEmptySports,
  onToggleSport,
  onToggleAll,
}: {
  eligibleEmptySports: Sport[];
  hiddenEmptySports: readonly Sport[];
  onToggleSport: (sport: Sport) => void;
  onToggleAll: () => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const hiddenSet = new Set(hiddenEmptySports);
  const hiddenEligibleCount = eligibleEmptySports.filter((s) => hiddenSet.has(s)).length;
  const anyHidden = hiddenEligibleCount > 0;
  const allHidden = hiddenEligibleCount === eligibleEmptySports.length; // eligible is non-empty here
  // aria-checked for the "All sports" row: 'true' when every eligible sport is
  // suppressed, 'mixed' when only some are, 'false' when none.
  const allAriaChecked: 'true' | 'false' | 'mixed' = allHidden ? 'true' : anyHidden ? 'mixed' : 'false';

  // Shared dismissal. Escape returns focus to the trigger so keyboard
  // users aren't stranded; an outside pointer does not, so focus stays
  // wherever the user just clicked.
  useDismissibleLayer({
    open,
    onDismiss: (reason) => {
      setOpen(false);
      if (reason === 'escape') triggerRef.current?.focus();
    },
    refs: [containerRef],
  });

  return (
    <div ref={containerRef} className="relative">
      <ChipGroup>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          className={cn(
            'flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
            anyHidden ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
          )}
        >
          Hide empty
          <ChevronDown size={13} className={cn('transition-transform', open && 'rotate-180')} />
        </button>
      </ChipGroup>

      {open && (
        <div
          role="menu"
          aria-label="Hide empty rows by sport"
          className="border-border bg-card absolute left-0 top-full z-20 mt-1 min-w-[11rem] rounded-lg border p-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={allAriaChecked}
            onClick={onToggleAll}
            className="text-foreground hover:bg-muted/60 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-semibold transition-colors"
          >
            <MenuCheck state={allAriaChecked} />
            All sports
          </button>
          <div className="bg-border/60 my-1 h-px" role="separator" />
          {eligibleEmptySports.map((sport) => {
            const on = hiddenSet.has(sport);
            return (
              <button
                key={sport}
                type="button"
                role="menuitemcheckbox"
                aria-checked={on}
                onClick={() => onToggleSport(sport)}
                className="text-foreground hover:bg-muted/60 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors"
              >
                <MenuCheck state={on ? 'true' : 'false'} />
                {SPORT_LABEL[sport]}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** 14px check-slot for the Hide-empty menu rows: filled check when on, a
 *  minus for the "All sports" partial state, empty otherwise. Kept aria-hidden
 *  — the row button's aria-checked is the accessible source of truth. */
function MenuCheck({ state }: { state: 'true' | 'false' | 'mixed' }) {
  return (
    <span className="text-primary flex h-3.5 w-3.5 shrink-0 items-center justify-center" aria-hidden>
      {state === 'true' && <Check size={13} />}
      {state === 'mixed' && <Minus size={13} />}
    </span>
  );
}

export function DashboardToolbar({
  selectedSport,
  selectedSports,
  activeSports,
  selectedMarketType,
  selectedDate,
  slateRangeLabel,
  slateRange,
  slateRangeEligible,
  onSlateRangeChange,
  isToday,
  dateFilter,
  statusFilter,
  marketSide,
  priceMode,
  showAllSportsChip,
  eligibleEmptySports,
  hiddenEmptySports,
  onToggleHiddenEmptySport,
  onToggleAllEmptySports,
  kalshiMarketsOnlyEligible,
  kalshiMarketsOnly,
  onToggleKalshiMarketsOnly,
  cfbBoardSelected,
  cfbSavedOnlyEligible,
  cfbSavedOnly,
  onToggleCfbSavedOnly,
  onSportToggle,
  onAllSportsClick,
  onMarketTypeChange,
  onDatePrev,
  onDateNext,
  onDateFilterChange,
  onTodayClick,
  onStatusFilterChange,
  onMarketSideChange,
  onPriceModeChange,
}: DashboardToolbarProps) {
  const selectedCfbBucket = cfbBucketOf(selectedSport);
  // One press of a chevron moves one window, so the buttons must say which.
  const weeklySlate = slateRangeEligible && slateRange === 'week';
  return (
    <div className="flex flex-col gap-2">
      {/* Row 1 — sport (left), market type (right, above Maker/Taker/Market) */}
      <div className="flex flex-wrap items-center gap-3">
        <ChipGroup className="min-w-0 max-w-full flex-wrap">
          {showAllSportsChip && (
            <Chip active={selectedSports.size === 0} onClick={onAllSportsClick}>
              All sports
            </Chip>
          )}
          {DASHBOARD_SPORT_TABS.filter((tab) => activeSports.has(tab.id)).map((tab) => (
            <Chip
              key={tab.id}
              // The CFB chip stays lit while one of its buckets is selected, so
              // the bucket group below reads as nested under a live parent
              // rather than as a second, competing selection.
              active={
                tab.id === 'cfb'
                  ? cfbBoardSelected
                  : showAllSportsChip
                    ? selectedSports.has(tab.id)
                    : selectedSport === tab.id
              }
              onClick={() => onSportToggle(tab.id)}
            >
              {tab.label}
            </Chip>
          ))}
        </ChipGroup>

        {cfbBoardSelected && (
          <ChipGroup className="min-w-0 max-w-full flex-wrap" label="CFB subdivision">
            {CFB_BUCKET_TABS.map((tab) => (
              <Chip
                key={tab.id}
                active={
                  tab.id === 'cfb' ? selectedCfbBucket === null : selectedSport === tab.id
                }
                onClick={() => onSportToggle(tab.id)}
              >
                {tab.label}
              </Chip>
            ))}
          </ChipGroup>
        )}

        <ChipGroup className="ml-auto">
          {MARKET_TYPE_TABS.map((tab) => (
            <Chip
              key={tab.id}
              active={selectedMarketType === tab.id}
              onClick={() => onMarketTypeChange(tab.id)}
              activeClass="bg-purple-600 text-white"
            >
              {tab.label}
            </Chip>
          ))}
        </ChipGroup>
      </div>

      {/* Row 2 — date / today-all / day-week / active-all-settled / YES-NO / price mode */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="bg-muted/50 border-border/50 flex items-center gap-1 rounded-lg border p-1">
          <button
            onClick={onDatePrev}
            aria-label={weeklySlate ? 'Previous week' : 'Previous day'}
            className="text-muted-foreground hover:text-foreground rounded p-1"
          >
            <ChevronLeft size={16} />
          </button>
          {/* Date text always picks up the primary color so it reads as
              the toolbar's anchor element. The Today chip's active
              state still gates on isToday — so scrolling off today
              dims the chip without making the date row look secondary. */}
          <div className="text-primary flex items-center gap-1.5 px-2 py-1 text-xs">
            <Calendar size={14} className="text-primary" />
            {/* whitespace-nowrap so a two-month range ("Dec 28, 2026 – Jan 3,
                2027") pushes the toolbar's own wrap instead of breaking the
                span across lines, where it would read as two dates. */}
            <span className="font-mono whitespace-nowrap">{slateRangeLabel ?? selectedDate}</span>
          </div>
          <button
            onClick={onDateNext}
            aria-label={weeklySlate ? 'Next week' : 'Next day'}
            className="text-muted-foreground hover:text-foreground rounded p-1"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Date filter. "Today" always jumps the calendar back to the
            actual current day and enforces single-day mode (cycling here
            would otherwise just toggle the filter without resetting the
            date). The chip is only highlighted when `isToday` — that is,
            when the displayed date IS actually today — so scrolling
            forward to tomorrow dims the chip even though dateFilter
            stays 'today' for the underlying single-day mode. "All"
            stays a pure filter toggle. */}
        <ChipGroup>
          <Chip active={isToday} onClick={onTodayClick}>
            Today
          </Chip>
          <Chip active={dateFilter === 'all'} onClick={() => onDateFilterChange('all')}>
            All
          </Chip>
        </ChipGroup>

        {/* #712 — how wide the bounded view is, on the football boards that
            have a weekly slate. Deliberately its own group beside Today/All
            rather than a third chip inside it: those two say WHETHER the view
            is bounded by a date, these say HOW WIDE that bound is, and "All"
            ignores the window entirely. Hidden, not disabled, off-board. */}
        {slateRangeEligible && (
          <ChipGroup label="Slate range">
            {(['day', 'week'] as SlateRange[]).map((r) => (
              <Chip key={r} active={slateRange === r} onClick={() => onSlateRangeChange(r)}>
                {r === 'day' ? 'Day' : 'Week'}
              </Chip>
            ))}
          </ChipGroup>
        )}

        <ChipGroup>
          {(['active', 'all', 'settled'] as StatusFilter[]).map((f) => (
            <Chip key={f} active={statusFilter === f} onClick={() => onStatusFilterChange(f)} activeClass="bg-primary text-primary-foreground">
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Chip>
          ))}
        </ChipGroup>

        {eligibleEmptySports.length > 0 && (
          <HideEmptyMenu
            eligibleEmptySports={eligibleEmptySports}
            hiddenEmptySports={hiddenEmptySports}
            onToggleSport={onToggleHiddenEmptySport}
            onToggleAll={onToggleAllEmptySports}
          />
        )}

        {/* #509 — venue-level row filter, deliberately a plain Chip rather than
            a menu: it is one boolean, not a per-sport set. Sits beside "Hide
            empty" because both answer "which rows do I want on screen", but the
            two are independent and neither toggle writes the other's setting.
            aria-pressed carries the on/off state; the primary fill is the
            "clear active state" the contract asks for. */}
        {kalshiMarketsOnlyEligible && (
          <ChipGroup>
            <button
              type="button"
              onClick={onToggleKalshiMarketsOnly}
              aria-pressed={kalshiMarketsOnly}
              title="Show only rows with an actionable Kalshi market. Rows carrying your position, open order, or an open ticket always stay visible."
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                kalshiMarketsOnly
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Kalshi markets only
            </button>
          </ChipGroup>
        )}

        {/* #654 — sits beside "Kalshi markets only" because both answer "which
            rows do I want on screen", and deliberately NOT inside the CFB
            subdivision group above: that group is a role="group" of
            subdivisions, and a saved filter is not one. Like its neighbour it
            is one boolean, so a plain Chip rather than a menu. */}
        {cfbSavedOnlyEligible && (
          <ChipGroup>
            <button
              type="button"
              onClick={onToggleCfbSavedOnly}
              aria-pressed={cfbSavedOnly}
              title="Show only the CFB games you've saved. Your saved list keeps its own rules — a saved game stays visible even when its book goes quiet."
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                cfbSavedOnly
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Saved only
            </button>
          </ChipGroup>
        )}

        {/* Right-justified: price mode first, then YES/NO */}
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <ChipGroup>
            <Chip
              active={priceMode === 'maker'}
              onClick={() => onPriceModeChange('maker')}
              activeClass="bg-primary text-primary-foreground"
            >
              Maker
            </Chip>
            <Chip
              active={priceMode === 'taker'}
              onClick={() => onPriceModeChange('taker')}
              activeClass="bg-primary text-primary-foreground"
            >
              Taker
            </Chip>
            <Chip
              active={priceMode === 'market'}
              onClick={() => onPriceModeChange('market')}
              activeClass="bg-primary text-primary-foreground"
            >
              Market
            </Chip>
          </ChipGroup>

          <ChipGroup>
            <Chip
              active={marketSide === 'yes'}
              onClick={() => onMarketSideChange('yes')}
              activeClass="bg-positive text-white"
            >
              YES
            </Chip>
            <Chip
              active={marketSide === 'no'}
              onClick={() => onMarketSideChange('no')}
              activeClass="bg-negative text-white"
            >
              NO
            </Chip>
          </ChipGroup>
        </div>
      </div>
    </div>
  );
}

